import { useState } from 'react'
import { MoreVertical } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useDeleteMenu } from '../hooks/use-menu-mutations'
import { menuDisplayName } from '../lib/menu-display'
import type { Menu } from '@/api/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

interface DeleteMenuDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  menu: Menu
  /** Called after a successful delete (e.g. navigate back to the list). */
  onDeleted?: () => void
}

export function DeleteMenuDialog({
  open,
  onOpenChange,
  menu,
  onDeleted,
}: DeleteMenuDialogProps) {
  const { t: tMenus } = useTranslation('menus')
  const deleteMenu = useDeleteMenu()

  const confirm = () => {
    deleteMenu.mutate(menu.id, {
      onSuccess: () => {
        onOpenChange(false)
        onDeleted?.()
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tMenus('list.deleteDialog.title')}</DialogTitle>
          <DialogDescription>
            {tMenus('list.deleteDialog.description', {
              name: menuDisplayName(menu),
            })}
          </DialogDescription>
        </DialogHeader>
        {menu.status === 'published' && (
          <p className="text-sm font-medium text-destructive">
            {tMenus('list.deleteDialog.publishedWarning')}
          </p>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteMenu.isPending}
          >
            {tMenus('list.deleteDialog.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={confirm}
            disabled={deleteMenu.isPending}
          >
            {deleteMenu.isPending && <Spinner className="w-4 h-4 mr-2" />}
            {tMenus('list.deleteDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface MenuDeleteMenuProps {
  menu: Menu
  variant?: 'ghost' | 'outline'
  onDeleted?: () => void
}

/**
 * The ⋮ overflow whose single entry deletes a menu, including the permission
 * gate and the confirmation dialog. Renders nothing without `menu:delete` or
 * for the master menu (which the server refuses to delete anyway).
 */
export function MenuDeleteMenu({
  menu,
  variant = 'outline',
  onDeleted,
}: MenuDeleteMenuProps) {
  const { t: tMenus } = useTranslation('menus')
  const { can } = usePermissions()
  const [deleteOpen, setDeleteOpen] = useState(false)

  if (menu.type === 'master' || !can(ACTIONS.MENU_DELETE)) return null

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} size="sm">
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => setDeleteOpen(true)}
            className="text-destructive"
          >
            {tMenus('list.actions.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteMenuDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        menu={menu}
        onDeleted={onDeleted}
      />
    </>
  )
}
